import { Accordion, AccordionItem, Button } from '@heroui/react';
import { Copy } from 'iconoir-react';
import { useCallback } from 'react';

import { agentLogDrawerSectionAccordionItemClasses } from './agentLogDrawerAccordionStyles';
import { formatAgentLogOutput } from './formatAgentLogOutput';

import type { AgentExecutionOutput } from '~/api/agentLogs/types';
import i18n from '~/common/helpers/i18n';
import { useCopyToClipboard } from '~/common/hooks/useCopyToClipboard';
import { Code } from '~/components/Code';

type AgentLogDetailsOutputSectionProps = {
  output: AgentExecutionOutput;
  isSectionExpanded: boolean;
  onSectionExpandedChange: (expanded: boolean) => void;
};

export function AgentLogDetailsOutputSection({
  output,
  isSectionExpanded,
  onSectionExpandedChange
}: AgentLogDetailsOutputSectionProps) {
  const formattedOutput = formatAgentLogOutput(output);
  const { copy } = useCopyToClipboard();

  const handleCopy = useCallback(() => {
    if (formattedOutput) {
      copy(formattedOutput);
    }
  }, [copy, formattedOutput]);

  return (
    <Accordion
      selectionMode='single'
      selectedKeys={isSectionExpanded ? new Set(['output']) : new Set<string>()}
      onSelectionChange={keys => {
        const selectedKeys =
          keys === 'all' ? new Set(['output']) : (keys as Set<string>);
        onSectionExpandedChange(selectedKeys.has('output'));
      }}
      itemClasses={agentLogDrawerSectionAccordionItemClasses}
      data-testid='monitor-agent-logs-details-output'
    >
      <AccordionItem
        key='output'
        aria-label={i18n.t('scenes.monitor.agentLogs.details.output')}
        title={
          <div className='flex w-full items-center justify-between gap-2 pr-2'>
            <span className='text-label-lg font-semibold text-on-surface'>
              {i18n.t('scenes.monitor.agentLogs.details.output')}
            </span>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onPress={handleCopy}
              isDisabled={!formattedOutput}
              aria-label={i18n.t('action.copy')}
              data-testid='monitor-agent-logs-details-output-copy'
            >
              <Copy className='h-5 w-5' strokeWidth={1.5} />
            </Button>
          </div>
        }
      >
        {!formattedOutput ? (
          <p className='text-body-regular-md text-on-surface-variant'>
            {i18n.t('scenes.monitor.agentLogs.details.noOutput')}
          </p>
        ) : (
          <Code
            readOnly
            value={formattedOutput}
            height='280px'
            enableJsonPathSearch
            disableJsonValidation
            lineWrapping
          />
        )}
      </AccordionItem>
    </Accordion>
  );
}
